import { BaseTool, IToolContext, IToolManifest } from '../../../core/tools/interfaces/ITool';
import type { ICommand } from '../../../core/commands/ICommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { Node } from '../../../core/models/scene';
import i18n from '../../../i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';
import { SessionMetrics } from '../../analytics/services/SessionMetrics';

const PERSON_SIZE = 60;
const SPACING = 120;

export class AddChildTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'genogram-add-child',
    name: i18n.t('genogram:tools.addChild', { defaultValue: 'Add Child' }) as string,
    icon: '👶',
    category: 'create',
    description: 'Add a child to the selected person',
    version: '1.0.0',
    supportedDiagramTypes: ['genogram' as any],
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    applicability: {
      contextTypes: ['person'],
      diagramTypes: ['genogram'],
    },
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: false,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    },
    shortcuts: [
      { key: '2', description: 'Add child (when one person selected)' }
    ]
  };

  constructor() {
    super();
    bindGenogramToolName(this.manifest, 'tools.addChild', 'Add Child');
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;

    // Check that selection is a single person
    const summary = context.selectionSummary;
    return summary?.contextType === 'person';
  }

  execute(): ICommand | null {
    if (!this.context) return null;

    const selectedId = Array.from(this.context.selection)[0];
    const state = useDiagramStore.getState();
    const selectedNode = state.nodes.find(n => n.id === selectedId);

    if (!selectedNode) return null;

    const pos = { x: selectedNode.x, y: selectedNode.y };
    const size = {
      width: (selectedNode as any).w ?? (selectedNode as any).width ?? PERSON_SIZE,
      height: (selectedNode as any).h ?? (selectedNode as any).height ?? PERSON_SIZE,
    } as { width: number; height: number };

    // Determine how many children already exist for this parent to avoid overlap
    const existingChildIds = state.edges
      .filter((e: any) => e.type === 'child' && e.from === selectedId)
      .map((e: any) => e.to);

    const childIndex = existingChildIds.length; // 0-based

    // Compute horizontal slot offset sequence: 0, -1, +1, -2, +2, ...
    const slotStep = Math.ceil(childIndex / 2);
    const slotDir = childIndex % 2 === 1 ? -1 : 1; // odd -> left, even -> right
    const slotSpacing = size.width + SPACING;
    const offsetX = childIndex === 0 ? 0 : slotDir * slotStep * slotSpacing;

    // Target position centered under parent, shifted by slot
    const parentCenterX = (pos.x ?? 0) + size.width / 2;
    const childX = Math.round(parentCenterX + offsetX - size.width / 2);
    const childY = (pos.y ?? 0) + size.height + SPACING;

    // Position child below in computed slot
    const newNodeId = `person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNode: Node = {
      id: newNodeId,
      type: 'person',
      name: i18n.t('genogram:relationships.child', { defaultValue: 'Child' }) as string,
      sex: 'unknown',
      x: childX,
      y: childY,
      w: size.width,
      h: size.height,
      width: size.width,
      height: size.height,
      category: 'genogram',
    } as Node;

    const nodeCommand = new CreateNodeCommand(newNode);

    // For child relationship, the edge goes from parent to child
    const edgeCommand = new CreateEdgeCommand({
      type: 'child',
      from: selectedId,
      to: newNodeId,
      category: 'genogram',
      fromAnchor: 'bottom',
      toAnchor: 'top',
      attrs: { __captionHidden: true },
    });

    // Track quick-add usage
    SessionMetrics.increment('quick_adds');
    SessionMetrics.firstTime('quick_add');
    
    return new CompositeCommand('Add Child', [nodeCommand, edgeCommand]);
  }
}
